import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, TimerReset, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
export default function OtpVerification() {
  const OTP_LENGTH = 6;
  const [otp, setOtp] = useState(new Array(OTP_LENGTH).fill(''));
  const [timer, setTimer] = useState(120);
  const [resendAvailable, setResendAvailable] = useState(false);
  const inputsRef = useRef([]);
  const navigate = useNavigate();
  useEffect(() => {
    if (timer === 0) {
      setResendAvailable(true);
      return;
    }
    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);

    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handleSubmit = () => {
    const fullOTP = otp.join('');
    if (fullOTP.length !== OTP_LENGTH) {
        toast.error('Please enter full OTP');
      return;
    }
    toast.success('OTP verified successfully!');
    navigate('/pre-filled-data')
    // TODO: API call
  };

  const handleResend = () => {
    setOtp(new Array(OTP_LENGTH).fill(''));
    setTimer(120);
    setResendAvailable(false);
    inputsRef.current[0].focus();
    toast.success('OTP resent!');
    // TODO: trigger resend logic
  };

  return (
    <div className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
    <ToastContainer/>
      {/* Background Video */}
      <video
        autoPlay
        muted
        loop
        className="absolute inset-0 w-full h-full object-cover z-0"
      >
        <source src="./assets/login_video.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-10" />

      {/* OTP Card */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-20 max-w-md w-full bg-white/20 backdrop-blur-xl border border-white/30 p-8 rounded-2xl shadow-2xl text-white"
      >
        <div className="flex items-center justify-center gap-3 mb-4">
          <ShieldCheck size={28} className="text-cyan-300" />
          <h2 className="text-2xl font-semibold">OTP Verification</h2>
        </div>

        <p className="text-sm text-center text-gray-100 mb-6">
          Enter the 6-digit code sent to your registered mobile/email
        </p>

        <div className="flex justify-center gap-3 mb-6">
          {otp.map((digit, index) => (
            <motion.input
              key={index}
              ref={(el) => (inputsRef.current[index] = el)}
              type="text"
              value={digit}
              maxLength={1}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className="w-12 h-12 text-center text-xl text-gray-800 font-bold bg-white rounded-md shadow-md focus:ring-2 focus:ring-cyan-400 outline-none"
              whileFocus={{ scale: 1.1 }}
            />
          ))}
        </div>

        <button
          onClick={handleSubmit}
          className="w-full bg-cyan-600 text-white py-2 rounded-md hover:bg-cyan-700 transition-all flex items-center justify-center gap-2"
        >
          <Smartphone size={18} />
          Verify OTP
        </button>

        <div className="mt-6 text-sm text-center">
          {resendAvailable ? (
            <button
              onClick={handleResend}
              className="text-cyan-400 hover:underline flex items-center justify-center gap-1"
            >
              <TimerReset size={16} />
              Resend OTP
            </button>
          ) : (
            <p className="text-gray-300">
              Resend in <span className="font-medium">{Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')}</span>
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
}
